import {
  ArrowLeft,
  CheckCircle2,
  Circle,
  FileText,
  FolderGit2,
  Link as LinkIcon,
  ListChecks,
  Plus,
  Sparkles,
  Target,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  addStudentProjectResult,
  generateStudentProjectResume,
  getStudentProject,
} from '../api.js'
import { useLanguage } from '../contexts/LanguageContext.jsx'
import {
  getStudentProjectById,
  pickStudentList,
  pickStudentText,
} from '../student-projects.js'

function safeText(value, fallback = '暂无说明') {
  const text = String(value || '').trim()
  if (!text || text === 'undefined' || text === 'null') return fallback
  return text
}

function safeList(value) {
  return Array.isArray(value) ? value.filter(Boolean) : []
}

function statusLabel(status) {
  if (status === 'completed' || status === 'done') return '已完成'
  if (status === 'paused') return '已暂停'
  if (status === 'archived') return '已归档'
  return '进行中'
}

function normalizeProject(payload) {
  const project = payload?.project || payload
  if (!project?.id) return null
  return {
    id: String(project.id),
    title: safeText(project.title, '未命名项目'),
    summary: safeText(project.summary, '从项目成长计划沉淀的项目成果。'),
    projectGoal: safeText(project.projectGoal || project.project_goal, '完成一个可展示、可写进简历的项目成果。'),
    status: safeText(project.status, 'active'),
    progress: Math.max(0, Math.min(Number(project.progress || 0), 100)),
    tasks: safeList(project.tasks),
    results: safeList(project.results),
    reports: safeList(project.reports),
    plan: project.plan && typeof project.plan === 'object' ? project.plan : {},
  }
}

function normalizeResumeReport(payload) {
  const content = payload?.content || payload?.report?.content || payload || {}
  return {
    resume: safeText(payload?.resume || content.resume, ''),
    interview: safeText(payload?.interview || content.interview, ''),
    suggestions: safeList(payload?.suggestions || content.suggestions),
    highlights: safeList(payload?.highlights || content.highlights || content.technicalHighlights),
    targetPosition: safeText(payload?.targetPosition || payload?.target_position || payload?.report?.targetPosition, ''),
  }
}

function CatalogProjectFallback({ projectId }) {
  const { language } = useLanguage()
  const project = getStudentProjectById(projectId)

  if (!project) {
    return (
      <main className="page-container student-project-detail-page">
        <Link className="secondary-button student-page-back" to="/projects">
          <ArrowLeft size={16} /> 返回我的项目成果
        </Link>
        <section className="student-project-detail-empty">
          <Target size={24} />
          <h1>没有找到这个项目</h1>
          <p>如果你刚加入项目成长计划，请登录后从我的项目成果进入。</p>
          <Link className="primary-button" to="/projects/recommendations">进入项目成长计划</Link>
        </section>
      </main>
    )
  }

  return (
    <main className="page-container student-project-detail-page">
      <Link className="secondary-button student-page-back" to="/projects">
        <ArrowLeft size={16} /> 返回项目与简历
      </Link>
      <section className="student-project-detail-hero">
        <span><FolderGit2 size={18} /> 项目模板</span>
        <h1>{pickStudentText(project.title, language)}</h1>
        <p>{pickStudentText(project.overview, language)}</p>
        <div className="student-project-detail-actions">
          <Link className="primary-button" to="/projects/recommendations">进入项目成长计划</Link>
          <Link className="secondary-button" to={`/projects/${project.id}/roadmap`}>
            <ListChecks size={16} /> 查看原学习路线
          </Link>
        </div>
      </section>
      <section className="student-project-detail-grid">
        <article>
          <h2>项目目标</h2>
          <p>{pickStudentText(project.finalOutput, language)}</p>
        </article>
        <article>
          <h2>项目成果</h2>
          <ul>{pickStudentList(project.displayMaterials, language).map((item) => <li key={item}>{item}</li>)}</ul>
        </article>
        <article>
          <h2>简历价值</h2>
          <p>{pickStudentText(project.resumeLine, language)}</p>
        </article>
        <article>
          <h2>项目任务</h2>
          <ol>{pickStudentList(project.sevenDayPlan, language).map((item) => <li key={item}>{item}</li>)}</ol>
        </article>
      </section>
    </main>
  )
}

function StudentProjectDetailPage() {
  const { projectId, id } = useParams()
  const resolvedId = projectId || id
  const shouldUseCatalogFallback = !/^\d+$/.test(String(resolvedId || ''))
  const [state, setState] = useState({ loading: true, error: '', project: null })
  const [resultDraft, setResultDraft] = useState({ title: '', content: '', url: '' })
  const [targetPosition, setTargetPosition] = useState('')
  const [resumeReport, setResumeReport] = useState(null)
  const [generatingResume, setGeneratingResume] = useState(false)
  const [savingResult, setSavingResult] = useState(false)
  const [notice, setNotice] = useState('')

  useEffect(() => {
    if (shouldUseCatalogFallback) {
      setState({ loading: false, error: '', project: null })
      return undefined
    }
    const controller = new AbortController()
    setState({ loading: true, error: '', project: null })
    getStudentProject(resolvedId, controller.signal)
      .then((payload) => setState({ loading: false, error: '', project: normalizeProject(payload) }))
      .catch((error) => {
        setState({
          loading: false,
          error: error?.message || '项目详情加载失败。',
          project: null,
        })
      })
    return () => controller.abort()
  }, [resolvedId, shouldUseCatalogFallback])

  const project = state.project
  const doneCount = useMemo(
    () => safeList(project?.tasks).filter((task) => ['done', 'completed'].includes(task.status)).length,
    [project],
  )
  const latestResumeReport = useMemo(() => {
    if (resumeReport) return resumeReport
    const report = safeList(project?.reports).find((item) => item.reportType === 'resume' || item.report_type === 'resume')
    return report ? normalizeResumeReport(report) : null
  }, [project, resumeReport])

  async function submitResult(event) {
    event.preventDefault()
    if (!project?.id || savingResult) return
    setSavingResult(true)
    setNotice('')
    try {
      const payload = await addStudentProjectResult(project.id, {
        title: resultDraft.title || '项目成果记录',
        content: resultDraft.content,
        url: resultDraft.url,
        resultType: resultDraft.url ? 'link' : 'note',
        progress: 80,
      })
      setState({ loading: false, error: '', project: normalizeProject(payload) })
      setResultDraft({ title: '', content: '', url: '' })
      setNotice('项目成果已保存，可以用于后续简历优化。')
    } catch (error) {
      setNotice(error?.message || '成果保存失败，请稍后重试。')
    } finally {
      setSavingResult(false)
    }
  }

  async function generateResumeReport() {
    if (!project?.id || generatingResume) return
    setGeneratingResume(true)
    setNotice('')
    try {
      const payload = await generateStudentProjectResume(project.id, { targetPosition })
      setResumeReport(normalizeResumeReport(payload))
      if (payload?.project) {
        setState({ loading: false, error: '', project: normalizeProject(payload.project) })
      }
      setNotice('简历描述已生成并保存。')
    } catch (error) {
      setNotice(error?.message || '生成失败，请稍后重试。')
    } finally {
      setGeneratingResume(false)
    }
  }

  if (!state.loading && !project) {
    return <CatalogProjectFallback projectId={resolvedId} />
  }

  return (
    <main className="page-container student-project-detail-page">
      <Link className="secondary-button student-page-back" to="/projects">
        <ArrowLeft size={16} /> 返回我的项目成果
      </Link>

      {state.loading ? (
        <section className="student-project-detail-empty">
          <FolderGit2 size={24} />
          <h1>正在加载项目详情...</h1>
          <p>项目任务和成果会在这里汇总。</p>
        </section>
      ) : null}

      {state.error ? <p className="student-projects-notice">{state.error}</p> : null}

      {project ? (
        <>
          <section className="student-project-detail-hero">
            <span><FolderGit2 size={18} /> {statusLabel(project.status)}</span>
            <h1>{project.title}</h1>
            <p>{project.summary}</p>
            <div className="student-project-detail-actions">
              <Link className="primary-button" to="/more/resume">
                <FileText size={16} /> 去优化简历
              </Link>
              <Link className="secondary-button" to="/growth">
                <ListChecks size={16} /> 回到成长中心
              </Link>
            </div>
          </section>

          <section className="student-project-detail-stats">
            <article>
              <span>当前进度</span>
              <strong>{project.progress}%</strong>
              <div className="student-project-progress"><i style={{ width: `${project.progress}%` }} /></div>
            </article>
            <article>
              <span>项目任务</span>
              <strong>{doneCount}/{project.tasks.length}</strong>
              <p>完成任务后，后续可沉淀为简历素材。</p>
            </article>
            <article>
              <span>项目成果</span>
              <strong>{project.results.length}</strong>
              <p>链接、总结、截图说明都可以先记录在这里。</p>
            </article>
          </section>

          <section className="student-project-detail-grid">
            <article>
              <h2>项目目标</h2>
              <p>{project.projectGoal}</p>
            </article>
            <article>
              <h2>项目分析</h2>
              <ul>
                {safeList(project.plan.analysis).length
                  ? safeList(project.plan.analysis).map((item) => <li key={item}>{item}</li>)
                  : <li>先完成项目任务，再补充成果记录，后续可作为简历优化素材。</li>}
              </ul>
            </article>
            <article>
              <h2>项目任务</h2>
              <div className="student-project-task-list">
                {project.tasks.length ? project.tasks.map((task) => {
                  const done = ['done', 'completed'].includes(task.status)
                  return (
                    <div key={task.id}>
                      {done ? <CheckCircle2 size={18} /> : <Circle size={18} />}
                      <span>{safeText(task.title, '项目任务')}</span>
                    </div>
                  )
                }) : <p>暂无项目任务，项目成长计划保存后会自动同步。</p>}
              </div>
            </article>
            <article>
              <h2>项目成果</h2>
              {project.results.length ? (
                <div className="student-project-result-list">
                  {project.results.map((result) => (
                    <section key={result.id}>
                      <strong>{safeText(result.title, '项目成果')}</strong>
                      <p>{safeText(result.content, '暂无成果说明')}</p>
                      {result.url ? <a href={result.url} target="_blank" rel="noreferrer"><LinkIcon size={15} /> 查看链接</a> : null}
                    </section>
                  ))}
                </div>
              ) : <p>还没有成果记录。可以先记录项目链接、截图说明、报告摘要或复盘。</p>}
            </article>
          </section>

          <section className="student-project-ai-career">
            <div>
              <span><Sparkles size={17} /> AI就业助手</span>
              <h2>把项目成果转成简历材料</h2>
              <p>先生成简历项目描述，并同步保存为项目 AI 报告。</p>
            </div>
            <label>
              <span>目标岗位</span>
              <input
                value={targetPosition}
                onChange={(event) => setTargetPosition(event.target.value)}
                placeholder="例如：数据分析实习生 / Java 后端实习"
              />
            </label>
            <div className="student-project-ai-actions">
              <button className="primary-button" type="button" onClick={generateResumeReport} disabled={generatingResume}>
                <FileText size={16} /> {generatingResume ? '正在生成...' : '生成简历描述'}
              </button>
            </div>
          </section>

          <section className="student-project-result-submit">
            <h2><Plus size={17} /> 记录新的项目成果</h2>
            <form onSubmit={submitResult}>
              <input value={resultDraft.title} onChange={(event) => setResultDraft((current) => ({ ...current, title: event.target.value }))} placeholder="成果标题" />
              <textarea value={resultDraft.content} onChange={(event) => setResultDraft((current) => ({ ...current, content: event.target.value }))} placeholder="写下完成了什么、结果如何、遇到什么问题" />
              <input value={resultDraft.url} onChange={(event) => setResultDraft((current) => ({ ...current, url: event.target.value }))} placeholder="成果链接，可选" />
              <button className="primary-button" type="submit" disabled={savingResult || (!resultDraft.content.trim() && !resultDraft.url.trim())}>
                {savingResult ? '保存中...' : '保存成果'}
              </button>
            </form>
          </section>

          {latestResumeReport ? (
            <section className="student-project-resume-report">
              <h2>简历描述</h2>
              {latestResumeReport.targetPosition ? <p>目标岗位：{latestResumeReport.targetPosition}</p> : null}
              <pre>{latestResumeReport.resume}</pre>
              {latestResumeReport.highlights.length ? <ul>{latestResumeReport.highlights.map((item) => <li key={item}>{item}</li>)}</ul> : null}
              {latestResumeReport.suggestions.length ? <ul>{latestResumeReport.suggestions.map((item) => <li key={item}>{item}</li>)}</ul> : null}
            </section>
          ) : null}

          {notice ? <p className="student-growth-error is-success">{notice}</p> : null}
        </>
      ) : null}
    </main>
  )
}

export default StudentProjectDetailPage
